import { Image, ImageStyle, StyleProp, TouchableOpacity, View, ViewStyle } from 'react-native'
import React, { ReactNode, useMemo } from 'react'
import { Icon, Pill, Text } from '@components/base'
import { StyleService, useStyleSheet } from '@style/service.ts'
import { Interval, Maybe, UiPillSize, UiPillType } from '@src/types.ts'
import { Feature, useFeatureFlag } from '@src/components/FeatureFlag'
import { formatCurrencyAmount } from '../../utils/utils'

export interface ProductSummaryProps {
  title: string
  description: string
  image?: { imageSource: any }
  imageStyle?: StyleProp<ViewStyle>
  imageTag?: ReactNode
  imageTagStyle?: StyleProp<ViewStyle>
  icon?: ReactNode
  onPress: () => void
  price?: number | null
  callout?: string | null
  showUnitPrice?: boolean
  startPrice?: boolean
  units?: Maybe<number>
  isCgm?: boolean
  interval?: Interval
  isInsuranceCovered?: boolean
}

export const ProductSummary = ({
  title,
  description,
  image,
  imageStyle,
  imageTag,
  imageTagStyle,
  icon,
  onPress,
  callout,
  price,
  showUnitPrice,
  startPrice,
  units,
  isCgm,
  interval,
  isInsuranceCovered = false,
}: ProductSummaryProps) => {
  const styles = useStyleSheet(themedStyle)
  const hasIcon = !!image || !!icon
  const alwaysShowPrice = useFeatureFlag(Feature.CoachHubAddonsAlwaysShowPrice)

  const priceText = useMemo(() => {
    if (callout) {
      return callout
    }
    if (showUnitPrice && price && units) {
      return `Only $${formatCurrencyAmount(price / units)} per ${isCgm ? 'sensor' : 'unit'}`
    }
    if (isInsuranceCovered && price !== null) {
      return `$${formatCurrencyAmount(price || 0)} out of pocket`
    }
    if (!alwaysShowPrice || !price) {
      return null
    }
    if (interval) {
      return `Only $${formatCurrencyAmount(price)} per ${interval.suffix}`
    }
    if (startPrice) {
      return `Starts from $${formatCurrencyAmount(price)}`
    }
    return `$${formatCurrencyAmount(price)}`
  }, [
    callout,
    showUnitPrice,
    price,
    units,
    isInsuranceCovered,
    alwaysShowPrice,
    interval,
    startPrice,
    isCgm,
  ])

  return (
    <TouchableOpacity accessibilityLabel={title} onPress={onPress} style={styles.card}>
      <View style={styles.rowText}>
        <Text type="large" bold>
          {title}
        </Text>
        <Text type="regular" style={styles.description}>
          {description}
        </Text>
        {priceText && (
          <View style={styles.pillContainer}>
            <Pill text={priceText} status={UiPillType.Success} size={UiPillSize.S} />
          </View>
        )}
      </View>

      {hasIcon && (
        <View style={[styles.rowImage, imageStyle]}>
          {image && <Image style={styles.image as ImageStyle} source={image.imageSource} />}
          {icon}
        </View>
      )}

      <View style={[styles.imageTag, imageTagStyle]}>{imageTag}</View>

      <Icon name="caret-right" weight="bold" style={styles.arrowIcon} />
    </TouchableOpacity>
  )
}

const themedStyle = StyleService.create({
  card: {
    width: '100%',
    flexDirection: 'row',
    borderColor: 'theme.surface.base',
    backgroundColor: 'theme.surface.base2',
    borderRadius: 24,
    borderWidth: 2,
  },
  rowText: {
    flex: 1,
    marginVertical: 24,
    marginLeft: 24,
    gap: 4,
  },
  rowImage: {
    flexDirection: 'row',
    alignSelf: 'flex-end',
    marginRight: 24,
    alignItems: 'center',
    justifyContent: 'center',
    width: 113,
    height: 123,
  },
  description: {
    color: 'theme.text.secondary',
  },
  arrowIcon: {
    position: 'absolute',
    right: 16,
    top: 16,
    width: 16,
    height: 16,
    color: 'theme.surface.base0',
  },
  image: {
    width: '100%',
    height: '100%',
    resizeMode: 'contain',
  },
  imageTag: {
    position: 'absolute',
    bottom: 0,
  },
  pillContainer: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: 8,
  },
})
